import { Controller } from "@hotwired/stimulus"

// Primer Brand's ActionMenu.
//
// The menu is in the markup from the start, hidden. Opening shows it under
// the button (above when there is no room below), moves focus to the first
// item and lets the arrow keys walk the items. Choosing an item dispatches
// `brand-action-menu:select` with the item's value on the root element.
const GAP = 8

export default class extends Controller {
  static targets = ["button", "menu"]
  static values = { alignment: { type: String, default: "end" }, open: { type: Boolean, default: false } }

  connect() {
    if (this.openValue) this.open()
  }

  toggle() {
    if (this.buttonTarget.disabled) return
    this.menuTarget.hidden ? this.open() : this.close()
  }

  open() {
    this.menuTarget.hidden = false
    this.position()
    this.menuTarget.classList.add("ActionMenu__menu--visible")
    this.buttonTarget.setAttribute("aria-expanded", "true")
    const first = this.items()[0]
    if (first) first.focus()
  }

  close() {
    if (this.menuTarget.hidden) return

    this.menuTarget.hidden = true
    this.menuTarget.classList.remove("ActionMenu__menu--visible", "ActionMenu__menu--pos-outside-bottom", "ActionMenu__menu--pos-outside-top")
    this.buttonTarget.setAttribute("aria-expanded", "false")
    this.buttonTarget.focus()
  }

  closeOutside(event) {
    if (this.element.contains(event.target)) return
    if (this.menuTarget.hidden) return

    this.menuTarget.hidden = true
    this.menuTarget.classList.remove("ActionMenu__menu--visible")
    this.buttonTarget.setAttribute("aria-expanded", "false")
  }

  // Up and down wrap around; Enter or Space activates the focused item.
  navigate(event) {
    const items = this.items()
    const index = items.indexOf(document.activeElement)

    if (event.key === "ArrowDown") {
      event.preventDefault()
      items[(index + 1) % items.length]?.focus()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      items[(index - 1 + items.length) % items.length]?.focus()
    } else if (event.key === "Enter" || event.key === " ") {
      const item = document.activeElement?.closest("li")
      if (!item || item.tagName !== "LI") return
      if (item.querySelector("a")) return // a link item follows its href on Enter

      event.preventDefault()
      this.choose(item)
    }
  }

  select(event) {
    const item = event.target.closest("li")
    if (!item || item.querySelector("a")) return

    this.choose(item)
  }

  choose(item) {
    if (item.getAttribute("aria-disabled") === "true") return

    if (item.getAttribute("role") === "menuitemradio") {
      for (const sibling of this.menuTarget.querySelectorAll("[role='menuitemradio']")) {
        sibling.setAttribute("aria-checked", sibling === item ? "true" : "false")
      }
    }

    this.dispatch("select", { detail: { value: item.dataset.value } })
    this.close()
  }

  // Split-button menus hold links; the anchors, not the list items, take focus.
  items() {
    const anchors = Array.from(this.menuTarget.querySelectorAll("a"))
    if (anchors.length > 0) return anchors

    return Array.from(this.menuTarget.querySelectorAll("li[role]"))
  }

  position() {
    const button = this.buttonTarget.getBoundingClientRect()
    const root = this.element.getBoundingClientRect()
    const menu = this.menuTarget
    const below = window.innerHeight - button.bottom - GAP >= menu.offsetHeight
    const side = below ? "outside-bottom" : "outside-top"

    menu.classList.remove("ActionMenu__menu--pos-outside-bottom", "ActionMenu__menu--pos-outside-top")
    menu.classList.add(`ActionMenu__menu--pos-${side}`)

    menu.style.top = below ? `${button.bottom - root.top + GAP}px` : `${button.top - root.top - menu.offsetHeight - GAP}px`
    menu.style.left = this.alignmentValue === "end"
      ? `${button.right - root.left - menu.offsetWidth}px`
      : `${button.left - root.left}px`
  }
}
